import * as React from 'react';
import { useContext } from 'react';
import { authenticationService } from '../../services/authentication';
import { PageRoutes } from '../root/Routes';
import { EventManager } from '../../services/EventManager';
import { LogManager } from '../../utils/LogManager';
import { WebportalEvent } from '../../services/WebportalEvent';
import './authentication.css';
import { QueryParamsContext } from '../contextProviders/QueryParamsProvider';
import { errorHandler } from '../../services/errorHandler';
import PasswordStep from './PasswordStep';
import EmailStep from './EmailStep';
import CodeConfirmation from './CodeConfirmation';
import { validateEmail } from 'src/webportal/services/validators/EmailValidator';
import { SESSION_STORAGE_EMAIL } from 'src/webportal/utils/constants';
import { MARKETING_DATA_KEYS, MarketingDataKey } from '../../interfaces/MarketingDataInterface';
import { MarketingAttributes } from '../../interfaces/MarketingAttributesInterface';

const logManager = new LogManager('Register');

interface RegisterState {
    email: string;
    password: string;
    isPasswordShown?: boolean;
    isEmailValid?: boolean;
    isPasswordValid?: boolean;
    isBusy?: boolean;
    errorMessage?: string | JSX.Element;
}

const FBP_COOKIE_KEY = '_fbp';

class Register extends React.Component<any, RegisterState> {

    constructor(props: any) {
        super(props);
        this.handleEmailState = this.handleEmailState.bind(this);

        this.state = {
            email: this.setSessionStorageEmail(),
            password: ''
        };
    }

    componentDidMount() {
        EventManager.fireCustomEvent(WebportalEvent.FloatLabels);
        authenticationService.setSocialLoginCallbacks(
            this.onSocialLoginSuccess,
            this.onSocialLoginFailure);
        this.setMarketingDataToLocalStorage();
    }

    render(): JSX.Element {
        const pathname = window.location.pathname;

        return (
            <div className="auth">
                {(pathname === PageRoutes.Register || pathname === `/webportal${PageRoutes.Register}`) &&
                    <EmailStep
                        history={this.props.history}
                        handleEmailState={this.handleEmailState}
                    />}
                {(pathname === PageRoutes.PasswordStep || pathname === `/webportal${PageRoutes.PasswordStep}`) &&
                    <PasswordStep
                        history={this.props.history}
                        email={this.state.email}
                        queryParams={this.props.queryParams}
                    />}
                {(pathname === PageRoutes.CodeConfirmation || pathname === `/webportal${PageRoutes.CodeConfirmation}`) &&
                    <CodeConfirmation email={this.state.email}/>}
            </div>
        );
    }

    private handleEmailState(email: string): void {
        this.setState({email: email});
    }

    private onSocialLoginSuccess(): void {
        this.props.history.push(PageRoutes.Dashboard);
    }

    private onSocialLoginFailure(error: any): void {
        logManager.error(error);

        let errorMessage = null;
        try {
            errorMessage = JSON.parse(error);

            switch (errorMessage.error) {
                case 'invalid_grant':
                    authenticationService.retrySocialLogin();
                    break;
                default:
                    errorHandler(error);
            }
        } catch (e) {
            errorHandler(e);
        }
    }

    private setSessionStorageEmail(): string {
        const sessionStorageEmail = sessionStorage.getItem(SESSION_STORAGE_EMAIL);
        if (sessionStorageEmail && validateEmail(sessionStorageEmail)) {
            sessionStorage.removeItem(SESSION_STORAGE_EMAIL);
            return sessionStorageEmail;
        }

        return '';
    }

    private getFbpCookieValue(): string | void {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.startsWith(FBP_COOKIE_KEY + '=')) {
                return cookie.substring(FBP_COOKIE_KEY.length + 1);
            }
        }
    }

    private setMarketingDataToLocalStorage(): void {
        const params = this.sanitizeMarketingData(
            new URLSearchParams(window.location.search)
        );
        const marketingData = this.buildMarketingAttributesFromQueryParams(params);

        localStorage.setItem('marketingData', JSON.stringify(marketingData));
    }

    private buildMarketingAttributesFromQueryParams(params: URLSearchParams): MarketingAttributes {
        return {
            utmData: {
                utmSource: params.get('utm_source') ?? '',
                utmMedium: params.get('utm_medium') ?? '',
                utmCampaign: params.get('utm_campaign') ?? '',
                utmId: params.get('utm_id') ?? '',
                utmTerm: params.get('utm_term') ?? '',
            },
            facebookClickId: params.get('fbclid') ?? '',
            linkedInFatId: params.get('li_fat_id') ?? '',
            googleClickId: params.get('gclid') ?? '',
            fbp: this.getFbpCookieValue() || '',
            userAgent: navigator.userAgent,
            partnerSource: params.get('referral') ?? '',
        };
    }

    private sanitizeMarketingData(params: URLSearchParams) {
        const result = new URLSearchParams();
        params.forEach((value, key) => {
            result.set(
                key,
                MARKETING_DATA_KEYS.includes(key as MarketingDataKey)
                    ? value.replace(/[^\w-]/g, '')
                    : value
            );
        });
        return result;
    }
}

const RegisterContainer = (props: any) => {
    const queryParams = useContext(QueryParamsContext);
    return <Register queryParams={queryParams} {...props}/>;
};

export default RegisterContainer;



// WEBPACK FOOTER //
// ./src/webportal/components/authentication/Register.tsx